import { useBackgroundTask } from 'components/BackgroundTaskModal/hooks';
import { ICONS } from 'consts/consts';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button } from 'reactstrap';
import { useConfirmApi, useProxyParameters } from 'utils/api/ApiHooks';
import ConfirmAPI from 'utils/api/ConfirmAPI';
import { getPrettyDate, sanitizeUrlQueryParams } from 'utils/util/util';
import SimpleErrorPage from 'views/Error/SimpleErrorPage';
import Page from 'views/Layout/Pages/Page';
import { useInfiniteScroll } from 'views/Objectives/ObjectivesOverviewPage/hooks';
import EmptyState from 'views/Widgets/EmptyState';
import Loading from 'views/Widgets/Loading';
import FilterablePeopleTable from 'views/Widgets/People/FilterablePeopleTable';
import { FORMAT_AVATAR_GROUP } from 'views/Widgets/People/Filters/common';
import { useAIAgentsHub } from '../AiAgentsHubContext';
import CreateAgentModal from './CreateAgentModal';
import { AGENT_STATUSES } from './consts';
import DeleteAgentModal from './DeleteAgentModal';
import EditAgentModal from './EditAgentModal';
import MoreActionsButton from './MoreActionsButton';

const AgentsAdminPage: FC = () => {
  const { setAction } = useAIAgentsHub();
  const { formatMessage, locale } = useIntl();
  const proxyParameters = useProxyParameters();
  const [currentUrl, setCurrentUrl] = useState<string>('agents');
  const [, setCurrentTotalCount] = useState<number | undefined>(undefined);
  const [nextUrl, setNextUrl] = useState<string | undefined>(undefined);
  const [accumulatedData, setAccumulatedData] = useState<any[] | undefined>(
    undefined
  );
  const [isLoadingMoreItems, setIsLoadingMoreItems] = useState<boolean>(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState<number | undefined>(
    undefined
  );
  const [initializingAgentId, setInitializingAgentId] = useState<
    number | undefined
  >(undefined);
  const [bgTaskKey, setBgTaskKey] = useState<string | null>(null);

  // Refs to track current values for use in callbacks (avoids stale closure issues)
  const initializingAgentIdRef = useRef<number | undefined>(undefined);

  // Keep refs in sync with state
  useEffect(() => {
    initializingAgentIdRef.current = initializingAgentId;
  }, [initializingAgentId]);

  const handleCreateClick = useCallback(() => {
    setIsCreateModalOpen(true);
  }, []);

  const handleEditClick = useCallback((agentId: number) => {
    setSelectedAgentId(agentId);
    setIsEditModalOpen(true);
  }, []);

  const handleDeleteClick = useCallback((agentId: number) => {
    setSelectedAgentId(agentId);
    setIsDeleteModalOpen(true);
  }, []);

  const handleInitializeClick = useCallback(
    (agentId: number) => {
      // Set refs synchronously before any async operations
      initializingAgentIdRef.current = agentId;
      setInitializingAgentId(agentId);
      ConfirmAPI.sendRequestToConfirm(
        'POST',
        `/agents/${agentId}/initialize`,
        proxyParameters,
        (response, error) => {
          if (error) {
            console.error('Error initializing agent:', error);
            initializingAgentIdRef.current = undefined;
            setInitializingAgentId(undefined);
            return;
          }
          if (response?.background_task_key) {
            setBgTaskKey(response.background_task_key);
            // Update the agent status in the list to show it's initializing
            setAccumulatedData((ad) =>
              ad?.map((agent) =>
                agent.id === agentId
                  ? { ...agent, status: 'A' } // A for INITIALIZING (matches backend status code)
                  : agent
              )
            );
          }
        }
      );
    },
    [proxyParameters]
  );

  const handleApplyModificationsClick = useCallback(
    (agentId: number) => {
      // Set refs synchronously before any async operations
      initializingAgentIdRef.current = agentId;
      setInitializingAgentId(agentId);
      ConfirmAPI.sendRequestToConfirm(
        'POST',
        `/agents/${agentId}/apply-modifications`,
        proxyParameters,
        (response, error) => {
          if (error) {
            console.error('Error applying modifications:', error);
            initializingAgentIdRef.current = undefined;
            setInitializingAgentId(undefined);
            return;
          }
          if (response?.background_task_key) {
            setBgTaskKey(response.background_task_key);
            // Update the agent status in the list to show it's initializing
            setAccumulatedData((ad) =>
              ad?.map((agent) =>
                agent.id === agentId
                  ? { ...agent, status: 'A' } // A for INITIALIZING
                  : agent
              )
            );
          }
        }
      );
    },
    [proxyParameters]
  );

  const handleDisableClick = useCallback(
    (agentId: number) => {
      ConfirmAPI.sendRequestToConfirm(
        'POST',
        `/agents/${agentId}/disable`,
        proxyParameters,
        (response, error) => {
          if (error) {
            console.error('Error disabling agent:', error);
            return;
          }
          // Update the agent status in the list to Draft
          setAccumulatedData((ad) =>
            ad?.map((agent) =>
              agent.id === agentId ? { ...agent, ...response } : agent
            )
          );
        }
      );
    },
    [proxyParameters]
  );

  const taskDescriptor = useMemo(
    () => ({
      task_key: bgTaskKey ?? '',
      status: 'submitted',
    }),
    [bgTaskKey]
  );

  const handleBgTaskSuccess = useCallback(() => {
    setBgTaskKey(null);
    // Update the agent status to Published (use ref for latest value)
    const agentId = initializingAgentIdRef.current;
    setAccumulatedData((ad) =>
      ad?.map((agent) =>
        agent.id === agentId ? { ...agent, status: 'P' } : agent
      )
    );
    // Clear refs and state
    initializingAgentIdRef.current = undefined;
    setInitializingAgentId(undefined);
  }, []);

  const handleBgTaskError = useCallback(() => {
    setBgTaskKey(null);
    // Revert agent status to previous state on error (use refs for latest values)
    const agentId = initializingAgentIdRef.current;
    setAccumulatedData((ad) =>
      ad?.map((agent) =>
        agent.id === agentId ? { ...agent, status: 'D' } : agent
      )
    );
    // Clear refs and state
    initializingAgentIdRef.current = undefined;
    setInitializingAgentId(undefined);
  }, []);

  useBackgroundTask({
    disabled: !bgTaskKey,
    taskDescriptor: taskDescriptor,
    onTaskCompleted: handleBgTaskSuccess,
    onError: handleBgTaskError,
  });

  const handleCloseCreateModal = useCallback(() => {
    setIsCreateModalOpen(false);
  }, []);
  const handleCloseEditModal = useCallback(() => {
    setIsEditModalOpen(false);
    setSelectedAgentId(undefined);
  }, []);
  const handleCloseDeleteModal = useCallback(() => {
    setIsDeleteModalOpen(false);
    setSelectedAgentId(undefined);
  }, []);

  const handleCallbackCreateModal = useCallback(
    (createdAgent) => {
      handleCloseCreateModal();
      setAccumulatedData((ad) => (ad ? [createdAgent, ...ad] : [createdAgent]));
    },
    [handleCloseCreateModal]
  );

  const handleCallbackEditModal = useCallback(
    (editedAgent) => {
      handleCloseEditModal();
      setAccumulatedData((ad) =>
        ad?.map((agent) => (agent.id === editedAgent.id ? editedAgent : agent))
      );
    },
    [handleCloseEditModal]
  );

  const handleCallbackDeleteModal = useCallback(
    (deletedAgent) => {
      handleCloseDeleteModal();
      setAccumulatedData((ad) =>
        ad?.filter((agent) => agent.id !== deletedAgent.id)
      );
    },
    [handleCloseDeleteModal]
  );

  const callback = useCallback(
    (
      data: { results: any[]; next?: string; count: number } | null,
      error,
      hardError
    ): void => {
      if (!data || error || hardError) {
        console.error('Error fetching feedback data:', error || hardError);
        return;
      }
      setAccumulatedData((prev) => [...(prev ?? []), ...data.results]);
      setNextUrl(
        data.next ? sanitizeUrlQueryParams(data.next, ['page']) : undefined
      );
      setCurrentTotalCount(data.count);
      setIsLoadingMoreItems(false);
    },
    []
  );
  const { error, status } = useConfirmApi<{
    results: any[];
    next?: string;
    count: number;
  }>({
    method: 'GET',
    url: currentUrl,
    callback: callback,
  });

  const loadMoreRef = useRef(null);

  const handleLoadMore = useCallback((nextUrl) => {
    setCurrentUrl(nextUrl);
    setIsLoadingMoreItems(true);
  }, []);

  useInfiniteScroll(
    loadMoreRef,
    useCallback(() => {
      if (nextUrl) {
        handleLoadMore(nextUrl);
      }
    }, [nextUrl, handleLoadMore])
  );

  const columns = useMemo(() => {
    return [
      {
        name: formatMessage({
          id: 'app.views.ai.agents.agents_admin_admin.table.name',
          defaultMessage: 'Name',
        }),
        field: 'name',
        className: 'col-1',
        sortable: false,
      },
      {
        name: formatMessage({
          id: 'app.views.ai.agents.agents_admin_admin.table.description',
          defaultMessage: 'Description',
        }),
        field: 'description',
        className: 'col-2',
        sortable: false,
      },
      {
        name: formatMessage({
          id: 'app.views.ai.agents.agents_admin_admin.table.owners',
          defaultMessage: 'Owners',
        }),
        field: 'owners',
        className: 'col-1',
        sortable: false,
        format: FORMAT_AVATAR_GROUP,
      },
      {
        name: formatMessage({
          id: 'app.views.ai.agents.agents_admin_admin.table.visibility',
          defaultMessage: 'Visibility',
        }),
        field: 'visibility',
        className: 'col-1',
        sortable: false,
      },
      {
        name: formatMessage({
          id: 'app.views.ai.agents.agents_admin_admin.table.status',
          defaultMessage: 'Status',
        }),
        field: 'status',
        className: 'col-1',
        sortable: false,
      },
      {
        name: formatMessage({
          id: 'app.views.ai.agents.agents_admin_admin.table.updated_at',
          defaultMessage: 'Last Updated',
        }),
        field: 'updated_at',
        format: (date: string) => {
          return getPrettyDate({
            dateString: date,
            includeTime: true,
            locale,
          });
        },
        className: 'col-2',
        sortable: false,
      },
      {
        name: formatMessage({
          id: 'app.views.ai.agents.agents_admin_admin.table.actions',
          defaultMessage: 'Actions',
        }),
        field: 'actions',
        className: 'col-1',
        sortable: false,
      },
    ];
  }, [formatMessage, locale]);

  const rows = useMemo(() => {
    if (!accumulatedData) {
      return [];
    }
    return accumulatedData.map((agent) => {
      const isInitializing =
        agent.id === initializingAgentId || agent.status === 'A';
      return {
        id: agent.id,
        name: agent.name,
        description: agent.description || (
          <em className="text-muted">
            <FormattedMessage
              id="app.views.ai.agents.agents_admin_admin.no_description"
              defaultMessage="No description"
            />
          </em>
        ),
        visibility: (
          <>
            <i
              className={
                agent.visibility === 'P'
                  ? ICONS.PUBLIC
                  : agent.visibility === 'I'
                    ? ICONS.PRIVATE
                    : ICONS.FILTER
              }
            />{' '}
            {agent.visibility === 'P' ? (
              <FormattedMessage
                id="app.views.ai.agents.agents_admin_admin.visibility.public"
                defaultMessage="Public"
              />
            ) : agent.visibility === 'I' ? (
              <FormattedMessage
                id="app.views.ai.agents.agents_admin_admin.visibility.private"
                defaultMessage="Private"
              />
            ) : (
              <FormattedMessage
                id="app.views.ai.agents.agents_admin_admin.visibility.custom"
                defaultMessage="Custom"
              />
            )}
          </>
        ),
        viewers: agent.viewers ? (
          <span className="badge bg-info text-white">
            <FormattedMessage
              id="app.views.ai.agents.agents_admin_admin.viewers.filtered"
              defaultMessage="Filtered"
            />
          </span>
        ) : (
          <span className="text-muted">
            <FormattedMessage
              id="app.views.ai.agents.agents_admin_admin.viewers.none"
              defaultMessage="None"
            />
          </span>
        ),
        created_at: agent.created_at,
        updated_at: agent.updated_at,
        owners: agent.owners,
        status: isInitializing ? (
          <span className="d-flex align-items-center">
            <i
              className="fe fe-refresh-cw me-2"
              style={{
                animation: 'spinner-border 1.5s linear infinite',
              }}
            />
            <FormattedMessage
              id="app.views.ai.agents.agents_admin_admin.status.publishing"
              defaultMessage="Publishing…"
            />
          </span>
        ) : (
          AGENT_STATUSES(formatMessage).find((s) => s.id === agent.status)?.name
        ),
        actions: (
          <MoreActionsButton
            agent={agent}
            onEditClick={handleEditClick}
            onDeleteClick={handleDeleteClick}
            onInitializeClick={handleInitializeClick}
            onApplyModificationsClick={handleApplyModificationsClick}
            onDisableClick={handleDisableClick}
          />
        ),
      };
    });
  }, [
    accumulatedData,
    initializingAgentId,
    handleEditClick,
    handleDeleteClick,
    handleInitializeClick,
    handleApplyModificationsClick,
    handleDisableClick,
    formatMessage,
  ]);

  const createAgentButton = useMemo(
    () => (
      <Button color="primary" onClick={handleCreateClick}>
        <FormattedMessage
          id="app.views.ai.agents.agents_admin_admin.create_button"
          defaultMessage="Create new agent"
        />
      </Button>
    ),
    [handleCreateClick]
  );

  useEffect(() => {
    setAction(createAgentButton);
  }, [createAgentButton, setAction]);

  if (status === 'LOADING') {
    return (
      <Loading
        layout="overlay"
        message={formatMessage({
          id: 'app.views.ai.agents.agents_admin_admin.loading',
          defaultMessage: 'Loading agents...',
        })}
      />
    );
  }

  if (error) {
    return <SimpleErrorPage error={error} />;
  }

  return (
    <Page
      embedded={true}
      title={formatMessage({
        id: 'app.views.ai.agents.agents_admin_admin.title.agents_admin',
        defaultMessage: 'Agents Admin',
      })}
    >
      {!isEditModalOpen && !isCreateModalOpen && rows.length === 0 && (
        <EmptyState
          title={formatMessage({
            id: 'app.views.ai.agents.agents_admin_admin.no_agents.title',
            defaultMessage:
              'You do not have any agents that you are eligible to view.',
          })}
          subtitle={formatMessage({
            id: 'app.views.ai.agents.agents_admin_admin.description',
            defaultMessage:
              'Manage agents for your organization. Agents can be private (only visible to the owners), public (visible to all organization members), or custom (you can set specific viewers who can access it).',
          })}
        >
          {createAgentButton}
        </EmptyState>
      )}
      {rows.length > 0 && (
        <>
          {!isEditModalOpen && !isCreateModalOpen && (
            <FilterablePeopleTable
              columns={columns}
              rows={rows}
              hideFilters={true}
              hideExportButton={true}
            />
          )}
          <div ref={loadMoreRef} />
          {isLoadingMoreItems && <Loading />}

          {selectedAgentId && isEditModalOpen && (
            <EditAgentModal
              isOpen={isEditModalOpen}
              toggle={handleCloseEditModal}
              agentId={selectedAgentId}
              onSuccess={handleCallbackEditModal}
            />
          )}
          {selectedAgentId && isDeleteModalOpen && (
            <DeleteAgentModal
              isOpen={isDeleteModalOpen}
              onClosed={handleCloseDeleteModal}
              agentId={selectedAgentId}
              onDelete={handleCallbackDeleteModal}
            />
          )}
        </>
      )}
      {isCreateModalOpen && (
        <CreateAgentModal
          isOpen={isCreateModalOpen}
          toggle={handleCloseCreateModal}
          onSuccess={handleCallbackCreateModal}
        />
      )}
    </Page>
  );
};

export default React.memo(AgentsAdminPage);
